import React, { useState, useEffect } from "react";
import Tooltip from '@mui/material/Tooltip';

function AwsForm() {
  const [token, setToken] = useState("");
  const [showPrtgPassword, setShowPrtgPassword] = useState(false);
  const [enablePrtg, setEnablePrtg] = useState(false);
  const SUBMIT_KEY = `awsSaasSubmitted_${token || "default"}`;
  const LAUNCH_URL_KEY = `awsLaunchUrl_${token || "default"}`;
  const [submitted, setSubmitted] = useState(false);
  const [launchStackUrl, setLaunchStackUrl] = useState("");
  const [prtgAuthType, setPrtgAuthType] = useState("passhash"); // "passhash" or "apikey"

  const togglePrtgPasswordVisibility = () => {
    setShowPrtgPassword(!showPrtgPassword);
  };

  // Function to get the token parameter from the URL
  // AWS Marketplace uses "x-amzn-marketplace-token", but also support "token" for manual testing
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const awsMarketplaceToken = urlParams.get("x-amzn-marketplace-token");
    const testToken = urlParams.get("token");
    const tokenParam = awsMarketplaceToken || testToken;
    if (tokenParam) {
      setToken(tokenParam);
    }
  }, []);

  useEffect(() => {
    if (sessionStorage.getItem(SUBMIT_KEY) === "yes") {
      setSubmitted(true);            // form was already sent in this tab
      // Restore the launch URL if it exists
      const savedUrl = sessionStorage.getItem(LAUNCH_URL_KEY);
      if (savedUrl) {
        setLaunchStackUrl(savedUrl);
      }
    }
  }, [SUBMIT_KEY, LAUNCH_URL_KEY]);

  // Function to handle form submission
  const handleSubmit = async (event) => {
    event.preventDefault();

    const formData = new FormData(event.target);
    // Convert form data to a JSON object
    const data = {
      name: formData.get("name"),
      company: formData.get("company"),
      email: formData.get("email"),
      token, // Include the token from the URL
      prtgip: formData.get("prtgip"),
      prtgport: formData.get("prtgport"),
      prtguser: formData.get("prtguser"),
      prtgpass: formData.get("prtgpass"),
      prtgprobeip: formData.get("prtgprobeip"),
      prtgprobename: formData.get("prtgprobename"),
      prtgauthtype: prtgAuthType,
    };

    if (!enablePrtg) {
      data.prtgip = "";
      data.prtgport = "";
      data.prtguser = "";
      data.prtgpass = "";
      data.prtgprobeip = "";
      data.prtgprobename = "";
      data.prtgauthtype = "";
    }

    console.log("Submitting data to backend:", data);

    // Use mock endpoint for testing if no token provided
    const endpoint = token 
      ? "https://acp.automonx.net:5000/aws_onboard"       // Real AWS Marketplace
      : "https://acp.automonx.net:5000/aws_onboard_mock"; // Testing without token

    console.log("Using endpoint:", endpoint);

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        const responseData = await response.json();
        if (responseData && responseData.launchStackUrl) {
          setLaunchStackUrl(responseData.launchStackUrl);
          // Persist the launch URL
          sessionStorage.setItem(LAUNCH_URL_KEY, responseData.launchStackUrl);
        }
        sessionStorage.setItem(SUBMIT_KEY, "yes");
        setSubmitted(true); 
        //alert("Form submitted successfully!");
      } else {
        // Log full error details
        console.error("Backend error:", response.status, response.statusText);
        try {
          const errorData = await response.json();
          console.error("Error data:", errorData);
          if (errorData && errorData.message) {
            alert(`Failed to submit the form (HTTP ${response.status}): ${errorData.message}`);
          } else {
            alert(`Failed to submit the form. HTTP ${response.status}: ${response.statusText}`);
          }
        } catch (parseError) {
          alert(`Failed to submit the form. HTTP ${response.status}: ${response.statusText} (Could not parse error response)`);
        }
      }
    } catch (error) {
      console.error("Error submitting the form:", error);
      alert("An error occurred. Please try again later: "+ error.toString());
    }
  };

  return (
    <div style={styles.container}>
      <img src="/logo_black.png" height="77" width="400" alt="AutoMonX logo" />
      <h1 style={styles.header}>Sensor Pack for AWS - SAAS Order</h1>
      {!token && (
        <div style={{
          padding: "10px 15px",
          backgroundColor: "#fff3cd",
          border: "1px solid #ffc107",
          borderRadius: "4px",
          marginBottom: "15px",
          color: "#856404"
        }}>
          <strong>🧪 Testing Mode:</strong> No AWS Marketplace token detected. Form will use mock endpoint.
        </div>
      )}
      <div style={styles.textContainer}>
        <p>You have reached this page after ordering the AutoMonX Sensor Pack for AWS SAAS. In order to deploy your SAAS offering, please fill the details below.</p>
        <p><b>Note:</b> AutoMonX onboarding will be completed securely via IAM Role during deployment (no AWS keys required).</p>
        <p>After submitting this form, you will receive a CloudFormation Launch Stack link to deploy AutoMonX in your AWS account. Your PRTG credentials (if provided) will be stored in an encrypted format in our database.</p>
      </div>
      {submitted ? (
        <div style={styles.successBox}>
          <p style={styles.successText}>
            Your form was successfully submitted!<br/>
            {launchStackUrl ? (
              "Click the button below to launch the AutoMonX deployment stack in your AWS account."
            ) : (
              "We are preparing your deployment link. If the launch button does not appear, please use the link in your confirmation email or contact support."
            )}
          </p>
          {launchStackUrl && (
            <a
              href={launchStackUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={styles.launchButton}
            >
              Launch AutoMonX Deployment Stack
            </a>
          )}
          <p style={{...styles.successText, fontSize: "14px", marginTop: "20px"}}>
            Check your mailbox for a confirmation email with additional details.
          </p>
        </div>
      ) : (
        <form id="awsOnboardForm" onSubmit={handleSubmit} style={styles.form}>
          <label htmlFor="name" style={styles.label}>Full name:</label>
          <input
            type="text"
            id="name"
            name="name"
            placeholder="Enter your full name"
            required
            style={styles.input}
          />

          <label htmlFor="company" style={styles.label}>Company name:</label>
          <input
            type="text"
            id="company"
            name="company"
            placeholder="Enter your company name"
            required
            style={styles.input}
          />

          <label htmlFor="email" style={styles.label}>Email:</label>
          <input
            type="email"
            id="email"
            name="email"
            placeholder="Enter your email"
            required
            style={styles.input}
          />

          <label style={styles.label}>
            <input
              type="checkbox"
              checked={enablePrtg}
              onChange={() => setEnablePrtg(!enablePrtg)}
              style={{ marginRight: "8px" }}
            />
            Add PRTG Connection
          </label>

          {enablePrtg && (
            <>
              <label htmlFor="prtgip" style={styles.label}>PRTG Core IP:</label>
              <input
                type="text"
                id="prtgip"
                name="prtgip"
                placeholder="PRTG public IP or Hostname"
                style={styles.input}
              />

              <label htmlFor="prtgport" style={styles.label}>PRTG Core Web interface Port:</label>
              <input
                type="text"
                id="prtgport"
                name="prtgport"
                placeholder="PRTG Core Web Port (i.e. 443)"
                style={styles.input}
              />

              <label htmlFor="prtguser" style={styles.label}>PRTG Username:</label>
              <input
                type="text"
                id="prtguser"
                name="prtguser"
                placeholder="Enter the PRTG username"
                style={styles.input}
              />

              <label style={styles.label}>Authentication Type:</label>
              <div style={styles.radioContainer}>
                <label style={styles.radioLabel}>
                  <input
                    type="radio"
                    value="passhash"
                    checked={prtgAuthType === "passhash"}
                    onChange={(e) => setPrtgAuthType(e.target.value)}
                    style={{ marginRight: "8px" }}
                  />
                  Passhash
                </label>
                <label style={styles.radioLabel}>
                  <input
                    type="radio"
                    value="apikey"
                    checked={prtgAuthType === "apikey"}
                    onChange={(e) => setPrtgAuthType(e.target.value)}
                    style={{ marginRight: "8px" }}
                  />
                  API Key
                </label>
              </div>

              <label htmlFor="prtgpass" style={styles.label}>
                PRTG {prtgAuthType === "passhash" ? "passhash" : "API Key"}:
              </label>
              <div style={styles.passwordContainer}>
                <input
                  type={showPrtgPassword ? "text" : "password"} // Toggle between password and text
                  id="prtgpass"
                  name="prtgpass"
                  placeholder={`PRTG ${prtgAuthType === "passhash" ? "passhash" : "API key"}`}
                  style={styles.input}
                />
                <button
                  type="button"
                  onClick={togglePrtgPasswordVisibility}
                  style={styles.eyeButton}
                  aria-label="Toggle password visibility"
                >
                  {showPrtgPassword ? "Hide" : "Show"}
                </button>
              </div>
              
              <label htmlFor="prtgprobeip" style={styles.label}>
                PRTG Probe IP:
                <Tooltip
                  title={
                    <>
                      You must provide the public IP of the PRTG Probe you plan to use to communicate with our SAAS offering. 
                      The IP of the probe will be added to our security systems. 
                      It can be the PRTG Core IP as well if you use the PRTG Local probe for monitoring.
                    </>
                  }
                >
                  <span style={styles.tooltipIcon}>?</span>
                </Tooltip>
                
              </label>
              <input
                type="text"
                id="prtgprobeip"
                name="prtgprobeip"
                placeholder="Enter your prtg probe ip"
                style={styles.input}
              />

              <label htmlFor="prtgprobename" style={styles.label}>
                PRTG Probe Name:
                <Tooltip
                  title={
                    <>
                      Enter the name of the PRTG Probe as it appears in PRTG UI - make sure to copy it from the Probe settings i.e. Local Probe.
                    </>
                  }
                >
                  <span style={styles.tooltipIcon}>?</span>
                </Tooltip>
                
              </label>
              <input
                type="text"
                id="prtgprobename"
                name="prtgprobename"
                placeholder="Enter your prtg probe name"
                style={styles.input}
              />
            </>
          )}

          <button type="submit" style={styles.button}>Submit</button>
        </form>
      )}
      <footer style={styles.footer}>
        For any questions email us at&nbsp;
        <a href="mailto:support@automonx.com">support@automonx.com</a>
      </footer>
    </div>
  );
}

// Styling
const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "40px 20px",
    fontFamily: "Segoe UI, sans-serif",
    backgroundColor: "#f0f4f8",
    minHeight: "100vh",
  },
  header: {
    marginBottom: "20px",
    color: "#2d2d2d",
    textAlign: "center",
    fontSize: "26px",
  },
  textContainer: {
    maxWidth: "700px",
    textAlign: "left",
    backgroundColor: "#ffffff",
    padding: "20px",
    borderRadius: "8px",
    marginBottom: "30px",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.05)",
    lineHeight: "1.6",
    color: "#333",
  },
  form: {
    maxWidth: "500px",
    width: "100%",
    background: "#ffffff",
    padding: "25px",
    borderRadius: "10px",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
  },
  label: {
    display: "block",
    marginBottom: "6px",
    fontWeight: 600,
    color: "#444",
    fontSize: "15px",
  },
  radioContainer: {
    display: "flex",
    gap: "20px",
    marginBottom: "18px",
  },
  radioLabel: {
    display: "flex",
    alignItems: "center",
    fontWeight: 400,
    fontSize: "14px",
  },
  passwordContainer: {
    position: "relative",
    width: "100%",
  },
  input: {
    width: "100%",
    padding: "10px 12px",
    border: "1px solid #ccc",
    borderRadius: "5px",
    marginBottom: "18px",
    fontSize: "14px",
    boxSizing: "border-box",
  },
  eyeButton: {
    position: "absolute",
    right: "10px",
    top: "20px",
    background: "transparent",
    transform: "translateY(-50%)",
    border: "none",
    cursor: "pointer",
    fontSize: "0.9rem",
    lineHeight: "1",
    padding: 0,
    color: "#0078d7",
  },
  button: {
    width: "100%",
    padding: "12px",
    background: "#0078d7",
    color: "white",
    border: "none",
    borderRadius: "6px",
    fontSize: "15px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "background 0.3s ease",
  },
  tooltipIcon: {
    display: "inline-block",
    marginLeft: "6px",
    color: "#0078d7",
    backgroundColor: "#e8f0fe",
    borderRadius: "50%",
    width: "16px",
    height: "16px",
    lineHeight: "16px",
    textAlign: "center",
    fontSize: "12px",
    fontWeight: "bold",
    cursor: "help",
  },
  successBox: {                   
    maxWidth: "500px",
    width: "100%",
    background: "#ffffff",
    padding: "40px 30px",
    borderRadius: "10px",
    boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
    textAlign: "center",
  },
  successText: {                 
    fontSize: "18px",
    lineHeight: "1.6",
    color: "#2d2d2d",
    margin: 0,
  },
  launchButton: {
    display: "inline-block",
    marginTop: "20px",
    padding: "14px 30px",
    background: "#FF9900",
    color: "#232F3E",
    textDecoration: "none",
    borderRadius: "6px",
    fontSize: "16px",
    fontWeight: "bold",
    cursor: "pointer",
    transition: "background 0.3s ease",
  },
  footer: {
    marginTop: "40px",
    fontSize: "14px",
    color: "#6b6b6b",
    textAlign: "center",
  },
};

export default AwsForm;
